import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateOnly } from "components/common/Date";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import type { PaymentExpectation } from "../types/payment-expectation.types";
import {
  cancelPaymentExpectation,
  getPaymentExpectations,
} from "../services/PaymentExpectationService";
import {
  isTerminalPaymentExpectationStatus,
  paymentExpectationSourceTypeLabelMap,
  paymentExpectationStatusMap,
} from "../constants/payment-expectation.constants";
import { formatCurrency } from "utils/currency";
import CancelExpectationModal from "../CancelExpectationModal";

function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

export function PaymentExpectationsTable({ onMutation }: { onMutation?: () => void }) {
  const tableRef = useRef<() => void>(null);
  const { can } = useAccess();
  const canRead = can(Permissions.PAYMENT_EXPECTATION.READ);
  const canUpdate = can(Permissions.PAYMENT_EXPECTATION.UPDATE);
  const canCancel = can(Permissions.PAYMENT_EXPECTATION.CANCEL);

  const [cancelTarget, setCancelTarget] = useState<PaymentExpectation | null>(
    null,
  );
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const handleCancel = useCallback(
    async (cancelReason: string) => {
      if (!cancelTarget) return;
      setCancelSubmitting(true);
      try {
        const res = await cancelPaymentExpectation(cancelTarget.id, {
          cancelReason: cancelReason || undefined,
        });
        if (res.success) {
          toast.success(res.message ?? "Expectation cancelled");
          setCancelTarget(null);
          tableRef.current?.();
          onMutation?.();
        }
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not cancel expectation"));
      } finally {
        setCancelSubmitting(false);
      }
    },
    [cancelTarget, onMutation],
  );

  const columns: ColumnDef<PaymentExpectation>[] = useMemo(
    () => [
      IndexColumn<PaymentExpectation>(),
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        cell: ({ row }) =>
          canRead ? (
            <Link
              to={`/finance/payment-expectations/${row.original.id}`}
              className="text-decoration-none link"
            >
              <CodeCell code={row.original.code} />
            </Link>
          ) : (
            <CodeCell code={row.original.code} />
          ),
      },
      {
        header: "Client",
        id: "client",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.client?.name ? (
            <span>{row.original.client.name}</span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      SourceColumn<PaymentExpectation>({
        header: "Source",
        resolve: (r) =>
          resolveMappedSourceCell(r.sourceType, r.sourceId, {
            typeLabelMap: paymentExpectationSourceTypeLabelMap,
            source: r.source ?? null,
            linkRequiresCode: true,
          }),
      }),
      {
        header: "Expected",
        accessorKey: "expectedAmount",
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(Number(row.original.expectedAmount)),
      },
      {
        header: "Received",
        accessorKey: "receivedAmount",
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(Number(row.original.receivedAmount)),
      },
      {
        header: "Balance",
        id: "balance",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const balance = Math.max(
            0,
            Number(row.original.expectedAmount) -
              Number(row.original.receivedAmount),
          );
          return (
            <span
              className={balance > 0 ? "text-danger fw-medium" : "text-success"}
            >
              {formatCurrency(balance)}
            </span>
          );
        },
      },
      {
        header: "Expected date",
        accessorKey: "expectedDate",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.expectedDate ? (
            <DateOnly date={row.original.expectedDate} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Follow-up",
        accessorKey: "followUpDate",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.followUpDate ? (
            <DateOnly date={row.original.followUpDate} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={paymentExpectationStatusMap}
          />
        ),
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isTerminal = isTerminalPaymentExpectationStatus(r.status);
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Expectation actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canRead ? (
                  <DropdownItem
                    tag={Link}
                    to={`/finance/payment-expectations/${r.id}`}
                  >
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canUpdate ? (
                  <DropdownItem
                    tag={Link}
                    to={`/finance/payment-expectations/edit/${r.id}`}
                    disabled={isTerminal}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canCancel ? (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      className="text-danger"
                      disabled={isTerminal}
                      onClick={() => setCancelTarget(r)}
                    >
                      <i className="ri-close-circle-line align-bottom me-2" />
                      Cancel
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canRead, canUpdate, canCancel],
  );

  return (
    <>
      <CancelExpectationModal
        show={cancelTarget != null}
        record={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        submitting={cancelSubmitting}
      />
      <TableContainer
        columns={columns}
        fetchApi={getPaymentExpectations}
        pageSize={10}
        globalSearch
        SearchPlaceholder="Search…"
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
      />
    </>
  );
}
